"use client";

import {
  lazy,
  startTransition,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { flushSync } from "react-dom";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  ArrowUpRight,
  Binary,
  BookOpenCheck,
  BrainCircuit,
  ChartNoAxesCombined,
  CheckCircle2,
  ChevronRight,
  CircleDot,
  ClipboardCheck,
  Cloud,
  Cpu,
  Database,
  Eye,
  Fingerprint,
  FlaskConical,
  Gauge,
  GitBranch,
  History,
  Info,
  KeyRound,
  LogOut,
  Layers3,
  LockKeyhole,
  Network,
  Orbit,
  PanelsTopLeft,
  Radar,
  RefreshCw,
  Route,
  Scale,
  SearchCheck,
  Server,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Target,
  Telescope,
  Timer,
  TrendingUp,
  TriangleAlert,
  UserCheck,
  WalletCards,
  Workflow,
  XCircle,
  Zap,
  type LucideIcon,
} from "lucide-react";

import {
  Badge,
  Button,
  Progress,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  Slider,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  ToggleGroup,
  ToggleGroupItem,
} from "./ui";
import { LiveWorldPanel, useNexusWorldData, type NexusWorldData } from "./LiveWorldPanel";
import { normalizeSystemPulseResponses, mergeSystemPulseStates } from "./systemPulse";
import { normalizeOperationsPulse, PIPELINE_STAGE_KEY_CANDIDATES } from "./operationsPulse";
import { normalizeLearningPulse } from "./learningPulse";
import { normalizeProofPulse } from "./proofPulse";
import { normalizeCapitalPulse } from "./capitalPulse";
import NexusLiveFabric from "./NexusLiveFabric";
import NexusNotificationCenter from "./NexusNotificationCenter";
import { adminFetch } from "../services/api";
import "./ui.css";
import "./nexus-base.css";
import "./nexus-worlds.css";
import "./nexus-portfolio.css";
import "./live-world.css";
import "./owner-workspace.css";

const DemoPortfolioWorld = lazy(() => import("./DemoPortfolioWorld"));
const NexusAgentCommunicationHub = lazy(() => import("./NexusAgentCommunicationHub"));

type ViewId =
  | "pulse"
  | "intelligence"
  | "capital"
  | "portfolio"
  | "agents"
  | "operations"
  | "learning"
  | "proof";

type ExperienceMode = "buyer" | "owner" | "operator";
type ProofState = "live" | "audit" | "contract" | "pending";
type Tone = "good" | "warn" | "bad" | "neutral" | "violet";

type UpstreamResult = {
  ok: boolean;
  status: number;
  data: unknown;
  error: string | null;
};

type LiveSnapshot = {
  generatedAt: string;
  source: string;
  infrastructure: {
    railway: {
      state: string;
      // Always `null` on the real /api/hqs-live contract (see api/hqs-live.js) —
      // this endpoint genuinely cannot supply these; the canonical service
      // counts come from systemPulse.serviceLive/serviceTotal instead.
      successfulServices: number | null;
      totalServices: number | null;
      memoryGb: number | null;
      snapshotAt: string;
    };
    vercel: {
      state: string;
      project: string;
      environment: string;
    };
  };
  summary: UpstreamResult;
  readiness: UpstreamResult;
  guardian: UpstreamResult;
  // True only once a real /api/hqs-live attempt has completed (success OR
  // failure). Every derived "cockpit" value (service count, Guardian mode,
  // risk-flag count, bridge state) must render a truthful "—"/"UNKNOWN"
  // placeholder while this is false, instead of the illustrative fallback
  // numbers this snapshot shape used to carry before the first real fetch.
  hasFetchedOnce: boolean;
};

type GuardianData = {
  engineStatus?: {
    healthy?: boolean;
    mode?: string;
    degradedEndpoints?: string[];
  };
  stabilityScore?: number;
  riskFlags?: string[];
  alerts?: Array<{ level?: string; source?: string; message?: string }>;
  marketSnapshot?: { count?: number; source?: string; stale?: boolean };
};

type NavItem = {
  id: ViewId;
  label: string;
  shortLabel: string;
  question: string;
  icon: LucideIcon;
  meta: string;
};

// "unknown" is the truthful pre-fetch state (see `hasFetchedOnce`) — it must
// never be silently folded into "degraded", which would imply a real,
// checked failure that hasn't actually happened yet.
type BridgeState = "connected" | "degraded" | "unknown";

// Mirrors the shape returned by `summarizeEndpointResults()` in
// `./nexusData.js` (a plain JS module, so re-declared here for typing).
type WorldSummary = {
  total: number;
  live: number;
  empty: number;
  unavailable: number;
  auth_expired: number;
  auth_forbidden: number;
  degraded: number;
  timeout: number;
  error: number;
};

// Genuinely neutral "not yet checked" shape used only until the first real
// /api/hqs-live attempt completes (see `hasFetchedOnce`). Unlike the previous
// hardcoded illustrative snapshot (which claimed "34/34 Services", a
// "degraded" Guardian and specific risk flags before any request had even
// been sent), every field here is either a structurally-empty upstream
// result (`status: 0, data: null`) or `0`/`""` and is ALWAYS gated behind
// `hasFetchedOnce` at render time so the UI shows "—"/"UNKNOWN" instead of
// presenting these placeholders as real backend state.
const EMPTY_UPSTREAM_RESULT: UpstreamResult = { ok: false, status: 0, data: null, error: null };

const INITIAL_SNAPSHOT: LiveSnapshot = {
  generatedAt: "",
  source: "",
  infrastructure: {
    railway: {
      state: "unknown",
      successfulServices: null,
      totalServices: null,
      memoryGb: null,
      snapshotAt: "",
    },
    vercel: {
      state: "unknown",
      project: "hqs-private-quant",
      environment: "production",
    },
  },
  summary: EMPTY_UPSTREAM_RESULT,
  readiness: EMPTY_UPSTREAM_RESULT,
  guardian: EMPTY_UPSTREAM_RESULT,
  hasFetchedOnce: false,
};

const NAV_ITEMS: NavItem[] = [
  {
    id: "pulse",
    label: "System Pulse",
    shortLabel: "Pulse",
    question: "Was ist jetzt wichtig?",
    icon: Gauge,
    meta: "LIVE",
  },
  {
    id: "intelligence",
    label: "Intelligence",
    shortLabel: "Signals",
    question: "Was erkennt HQS?",
    icon: Radar,
    meta: "24·7·30",
  },
  {
    id: "capital",
    label: "Capital Field",
    shortLabel: "Capital",
    question: "Was darf Kapital?",
    icon: WalletCards,
    meta: "PAPER",
  },
  {
    id: "portfolio",
    label: "Demo Portfolio",
    shortLabel: "Portfolio",
    question: "Was hält HQS?",
    icon: Layers3,
    meta: "TWIN",
  },
  {
    id: "agents",
    label: "Agent Mesh",
    shortLabel: "Agents",
    question: "Wer prüft was?",
    icon: BrainCircuit,
    meta: "6",
  },
  {
    id: "operations",
    label: "Operations Twin",
    shortLabel: "Ops",
    question: "Läuft die Kette?",
    icon: Workflow,
    meta: "8",
  },
  {
    id: "learning",
    label: "Learning Ledger",
    shortLabel: "Learning",
    question: "Wird HQS besser?",
    icon: GitBranch,
    meta: "SHADOW",
  },
  {
    id: "proof",
    label: "Proof & Dossier",
    shortLabel: "Proof",
    question: "Was ist beweisbar?",
    icon: Fingerprint,
    meta: "AUDIT",
  },
];

const BUYER_CHAPTERS = [
  { id: "buyer-nexus", label: "HQS in 90 Sekunden", icon: Target, meta: "01" },
  { id: "buyer-decision", label: "Entscheidungskette", icon: Route, meta: "02" },
  { id: "buyer-proof", label: "Beweise & Reife", icon: Fingerprint, meta: "03" },
  { id: "buyer-dossier", label: "Due Diligence", icon: ClipboardCheck, meta: "04" },
] as const;

// fix/system-pulse-security-and-live-truth, Aufgabe 5: this used to carry a
// fabricated per-stage "audit readiness" percentage (99/96/94/91/74/71/86/82)
// with no backing measurement anywhere in the system. No live per-stage
// readiness score is connected yet, so the numeric claim is removed rather
// than replaced with another invented number — the UI now shows the honest
// "Aktueller Wert nicht geliefert" instead of a percentage. `state`
// (ready/watch/safe) remains a structural/qualitative stage classification,
// not a numeric production metric.
const PIPELINE_STAGES = [
  { name: "Daily Price Ingestion", short: "Prices", detail: "Preise · Universumsgröße nicht eindeutig verfügbar", state: "ready" },
  { name: "Snapshot Scan", short: "World", detail: "Marktzustand & Frische", state: "ready" },
  { name: "HQS Score Rebuild", short: "Score", detail: "Faktoren & Metrics", state: "ready" },
  { name: "Forecast Input", short: "Forecast", detail: "Agentenprognosen & Lineage", state: "ready" },
  { name: "Forecast Verification", short: "Verify", detail: "24h · 7d · 10td · 30d", state: "watch" },
  { name: "Outcome Tracking", short: "Outcome", detail: "Canonical Accuracy", state: "watch" },
  { name: "Forward Learning", short: "Learn", detail: "Gewichte · Shadow Mode", state: "ready" },
  { name: "Demo Portfolio", short: "Capital", detail: "Paper-only Execution", state: "safe" },
] as const;

// fix/system-pulse-security-and-live-truth, Aufgabe 5: "Verifiziert
// (Snapshot)" and "Canonical Accuracy" used to be static verified-share/
// accuracy percentage literals with no real source. Both are now computed from the same
// already-fetched operator-summary forecastQuality section systemPulse.js
// exposes as `forecastQuality.verifiedPct`/`forecastQuality.accuracyPct` —
// real numbers when the backend delivers them, "Aktueller Wert nicht
// geliefert" otherwise, never a reused/estimated value.
function buildForecastAudit(forecastQuality: { accuracyPct: number | null; verifiedPct: number | null }): Array<{ label: string; value: string; detail: string; tone: Tone }> {
  return [
    { label: "Forecasts gesamt", value: "Historischer Bestand", detail: "Audit-Snapshot · nicht als aktueller Performancebeweis verwendbar", tone: "neutral" },
    {
      label: "Verifiziert (Anteil)",
      value: forecastQuality.verifiedPct !== null ? formatNullablePercent(forecastQuality.verifiedPct) : NOT_DELIVERED,
      detail: forecastQuality.verifiedPct !== null ? "Anteil bereits verifizierter Forecasts am aktuellen Bestand" : NOT_DELIVERED_LONG,
      tone: forecastQuality.verifiedPct !== null ? "good" : "neutral",
    },
    { label: "Post-Cutover Nachweis", value: "—", detail: "Cutover-Nachweis nicht verfügbar", tone: "warn" },
    {
      label: "Canonical Accuracy",
      value: forecastQuality.accuracyPct !== null ? formatNullablePercent(forecastQuality.accuracyPct) : NOT_DELIVERED,
      detail: forecastQuality.accuracyPct !== null ? "Live aus Operator Summary · Forecast Quality" : NOT_DELIVERED_LONG,
      tone: forecastQuality.accuracyPct !== null ? "violet" : "neutral",
    },
  ];
}

const PROOF_LEGEND: Array<{ state: ProofState; label: string; detail: string }> = [
  { state: "live", label: "Live", detail: "Aktuell vom Read-Path geladen" },
  { state: "audit", label: "Audit", detail: "Geprüfter Snapshot mit Stichtag" },
  { state: "contract", label: "Vertrag", detail: "Im Code nachgewiesene Fähigkeit" },
  { state: "pending", label: "Offen", detail: "Noch nicht live bewiesen" },
];

const VIEW_TITLES: Record<ViewId, string> = {
  pulse: "SYSTEM PULSE",
  intelligence: "INTELLIGENCE FIELD",
  capital: "CAPITAL GOVERNOR",
  portfolio: "DEMO PORTFOLIO TWIN",
  agents: "AGENT MESH",
  operations: "OPERATIONS TWIN",
  learning: "LEARNING LEDGER",
  proof: "PROOF & DOSSIER",
};

function runNexusViewTransition(update: () => void) {
  const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const transitionDocument = document as Document & {
    startViewTransition?: (callback: () => void) => { finished: Promise<void> };
  };
  if (!reducedMotion && typeof transitionDocument.startViewTransition === "function") {
    transitionDocument.startViewTransition(() => flushSync(update));
    return;
  }
  startTransition(update);
}

function formatTime(value: string | undefined) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone: "UTC",
  }).format(date);
}

function formatDateTime(value: string | undefined) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  }).format(date);
}

// fix/system-pulse-security-and-live-truth: shared null-safe formatters for
// every nullable production value rendered in this file. A valid finite
// number is always formatted; `null`/`undefined`/`NaN` NEVER fabricates a 0
// or crashes (e.g. a bare `.toFixed()` on a null memoryGb) and instead
// renders the honest "—" (or, where a longer label fits, "Aktueller Wert
// nicht geliefert").
const NOT_DELIVERED = "—";
const NOT_DELIVERED_LONG = "Aktueller Wert nicht geliefert";

function formatNullableCount(value: number | null | undefined, total?: number | null): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return NOT_DELIVERED;
  if (typeof total === "number" && Number.isFinite(total)) return `${value}/${total}`;
  return String(value);
}

function formatNullableDecimal(value: number | null | undefined, digits: number, unit = ""): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return NOT_DELIVERED;
  return `${value.toFixed(digits)}${unit}`;
}

function formatNullablePercent(value: number | null | undefined, digits = 1): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return NOT_DELIVERED;
  return `${value.toFixed(digits).replace(".", ",")} %`;
}

function StatusDot({ tone = "neutral" }: { tone?: Tone }) {
  return <span className={`status-dot status-dot--${tone}`} aria-hidden="true" />;
}

function ProofChip({ state, compact = false }: { state: ProofState; compact?: boolean }) {
  const labels: Record<ProofState, string> = {
    live: "LIVE",
    audit: "AUDIT",
    contract: "CODE CONTRACT",
    pending: "PROOF PENDING",
  };
  return (
    <span className={`proof-chip proof-chip--${state} ${compact ? "proof-chip--compact" : ""}`}>
      <i /> {labels[state]}
    </span>
  );
}

function SectionHeading({
  kicker,
  title,
  detail,
  action,
}: {
  kicker: string;
  title: string;
  detail?: string;
  action?: ReactNode;
}) {
  return (
    <div className="section-heading">
      <div>
        <span className="section-kicker">{kicker}</span>
        <h2>{title}</h2>
        {detail ? <p>{detail}</p> : null}
      </div>
      {action ? <div className="section-heading__action">{action}</div> : null}
    </div>
  );
}

function MetricTile({
  label,
  value,
  detail,
  icon: Icon,
  tone = "neutral",
  proof,
}: {
  label: string;
  value: string;
  detail: string;
  icon: LucideIcon;
  tone?: Tone;
  proof?: ProofState;
}) {
  return (
    <article className={`metric-tile metric-tile--${tone}`}>
      <div className="metric-tile__top">
        <span className="metric-tile__icon"><Icon /></span>
        {proof ? <ProofChip state={proof} compact /> : <StatusDot tone={tone} />}
      </div>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function PageIntro({
  kicker,
  title,
  description,
  proof = "contract",
  children,
}: {
  kicker: string;
  title: string;
  description: string;
  proof?: ProofState;
  children?: ReactNode;
}) {
  return (
    <section className="page-intro">
      <div>
        <div className="page-intro__meta">
          <span className="section-kicker">{kicker}</span>
          <ProofChip state={proof} />
        </div>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {children ? <div className="page-intro__aside">{children}</div> : null}
    </section>
  );
}

function NexusCore({
  bridgeState,
  guardianMode,
  serviceCount,
  totalServices,
  activeLayer,
  onLayerChange,
}: {
  bridgeState: BridgeState;
  guardianMode: string;
  serviceCount: number | null;
  totalServices: number | null;
  activeLayer: string;
  onLayerChange: (layer: string) => void;
}) {
  const layers = [
    { id: "data", label: "DATA", sub: "FOUNDATION", icon: Database, className: "nexus-node--data" },
    { id: "forecast", label: "FORECAST", sub: "TRUTH", icon: Radar, className: "nexus-node--forecast" },
    { id: "agents", label: "AGENTS", sub: "CONSENSUS", icon: BrainCircuit, className: "nexus-node--agents" },
    { id: "capital", label: "CAPITAL", sub: "GOVERNOR", icon: Scale, className: "nexus-node--capital" },
    { id: "proof", label: "PROOF", sub: "LEDGER", icon: Fingerprint, className: "nexus-node--proof" },
  ];

  return (
    <div className={`nexus-core nexus-core--${bridgeState}`} aria-label="Interaktiver HQS Intelligence Core">
      <div className="nexus-grid" aria-hidden="true" />
      <div className="nexus-halo nexus-halo--one" aria-hidden="true" />
      <div className="nexus-halo nexus-halo--two" aria-hidden="true" />
      <div className="nexus-halo nexus-halo--three" aria-hidden="true" />
      <div className="nexus-scan" aria-hidden="true" />
      <div className="nexus-core__center">
        <span className="nexus-core__micro">HQS / NEXUS</span>
        <div className="nexus-core__orb"><Orbit /></div>
        <strong>{bridgeState === "connected" ? "COHERENT" : bridgeState === "unknown" ? "CHECKING" : "DEGRADED"}</strong>
        <span>{serviceCount !== null && totalServices !== null ? `${serviceCount}/${totalServices}` : "—"} SERVICES</span>
        <small>GUARDIAN · {guardianMode.toUpperCase()}</small>
      </div>
      {layers.map((layer) => {
        const Icon = layer.icon;
        return (
          <button
            type="button"
            key={layer.id}
            className={`nexus-node ${layer.className} ${activeLayer === layer.id ? "active" : ""}`}
            onClick={() => onLayerChange(layer.id)}
            aria-pressed={activeLayer === layer.id}
          >
            <span><Icon /></span>
            <strong>{layer.label}</strong>
            <small>{layer.sub}</small>
          </button>
        );
      })}
      <div className="nexus-core__legend">
        <span><i className="good" /> Operational</span>
        <span><i className="warn" /> Attention</span>
        <span><i className="violet" /> Intelligence</span>
      </div>
    </div>
  );
}

function DecisionSpine({ compact = false }: { compact?: boolean }) {
  const steps = [
    { icon: Database, label: "Observe", detail: "Prices · News", state: "ready" },
    { icon: ShieldCheck, label: "Validate", detail: "Freshness · Lineage", state: "ready" },
    { icon: Radar, label: "Forecast", detail: "24h · 7d · 10td · 30d", state: "ready" },
    { icon: BrainCircuit, label: "Challenge", detail: "Multi-Agent", state: "watch" },
    { icon: Scale, label: "Govern", detail: "Risk Gate", state: "safe" },
    { icon: WalletCards, label: "Execute", detail: "Paper only", state: "safe" },
    { icon: GitBranch, label: "Learn", detail: "Outcome · Weights", state: "watch" },
  ];

  return (
    <div className={`decision-spine ${compact ? "decision-spine--compact" : ""}`}>
      <div className="decision-spine__beam" aria-hidden="true"><i /></div>
      {steps.map((step, index) => {
        const Icon = step.icon;
        return (
          <div className={`decision-step decision-step--${step.state}`} key={step.label}>
            <span className="decision-step__index">{String(index + 1).padStart(2, "0")}</span>
            <span className="decision-step__icon"><Icon /></span>
            <strong>{step.label}</strong>
            <small>{step.detail}</small>
          </div>
        );
      })}
    </div>
  );
}

// Never a static/historical placeholder: `null` means the backend genuinely
// did not deliver a current value this fetch, and must render as an
// explicit "not delivered" state, never as a reused old number.
function formatForecastQueueFact(forecastQueueOpen: number | null): string {
  return forecastQueueOpen !== null
    ? `${forecastQueueOpen} Forecasts offen`
    : "Offene Forecasts: Aktueller Wert nicht geliefert";
}

function AskNexus({ bridgeState, forecastQueueOpen }: { bridgeState: BridgeState; forecastQueueOpen: number | null }) {
  const questions = [
    "Wie steht es um den Read-Path?",
    "Welche Stufe begrenzt das Lernen?",
    "Zeige die wichtigste Kapitalgrenze",
  ];
  const [selected, setSelected] = useState(questions[0]);

  const response = useMemo(() => {
    if (selected.includes("Read-Path")) {
      return {
        icon: Network,
        title: bridgeState === "connected" ? "Read-Path antwortet" : bridgeState === "unknown" ? "Erster Abruf läuft" : "Frontend Summary nicht bereit",
        lead: bridgeState === "connected"
          ? "Die verbundenen Read-Modelle antworten im aktuellen Abruf."
          : bridgeState === "unknown" ? "Ein Verbindungsstatus liegt noch nicht vor." : "Die Oberfläche erreicht den sicheren Summary-Pfad nicht vollständig.",
        facts: bridgeState === "unknown" ? ["Bewertung nach Abschluss des Abrufs"] : ["Status aus System Pulse", "Einzelne Antworten in der Datenlage einsehbar"],
        proof: (bridgeState === "unknown" ? "pending" : "live") as ProofState,
      };
    }
    if (selected.includes("Lernen")) {
      return {
        icon: GitBranch,
        title: "Verification ist die Engstelle",
        lead: "Gewichtslernen darf erst auf kanonisch verifizierte Outcomes reagieren.",
        facts: [formatForecastQueueFact(forecastQueueOpen), "Shadow Mode aktiv", "Keine direkte Live-Mutation"],
        proof: "audit" as ProofState,
      };
    }
    return {
      icon: ShieldCheck,
      title: "Capital Governor hat Vorrang",
      lead: "Eine Chance darf das Portfolio nur erreichen, wenn Vertrauen, Regime und Risikobudget genügen.",
      facts: ["Paper-only Execution", "NO_EDGE ist gültig", "Portfolio-Risiko kann blockieren"],
      proof: "contract" as ProofState,
    };
  }, [bridgeState, forecastQueueOpen, selected]);
  const Icon = response.icon;

  return (
    <article className="ask-nexus liquid-panel">
      <div className="ask-nexus__header">
        <span className="ask-nexus__mark"><Sparkles /></span>
        <div>
          <span className="section-kicker">GENERATIVE INTELLIGENCE SURFACE</span>
          <strong>Ask HQS NEXUS</strong>
        </div>
        <ProofChip state={response.proof} compact />
      </div>
      <div className="ask-nexus__questions">
        {questions.map((question) => (
          <button
            type="button"
            key={question}
            className={selected === question ? "active" : ""}
            onClick={() => setSelected(question)}
          >
            {question}<ChevronRight />
          </button>
        ))}
      </div>
      <div className="ask-nexus__result">
        <span className="ask-nexus__result-icon"><Icon /></span>
        <div>
          <span>STRUCTURED ANSWER</span>
          <h3>{response.title}</h3>
          <p>{response.lead}</p>
          <ul>
            {response.facts.map((fact) => <li key={fact}><CheckCircle2 /> {fact}</li>)}
          </ul>
        </div>
      </div>
      <div className="ask-nexus__footer">
        <Info /> Diese Oberfläche ordnet vorhandene HQS-Zustände. Sie führt keine Backend-Aktion aus.
      </div>
    </article>
  );
}

function PulseView({ snapshot, mode, worldSummary, systemPulse }: { snapshot: LiveSnapshot; mode: ExperienceMode; worldSummary: WorldSummary; systemPulse: ReturnType<typeof normalizeSystemPulseResponses> }) {
  // "checking" is the only genuinely pre-fetch canonical status (see
  // computeOverallStatus's `allChecking` branch) — `worldSummary.total` is
  // NOT a safe proxy for "has fetched": useNexusWorldData immediately fills
  // `results` with seven `state: "loading"` placeholders the instant a
  // fetch starts, well before any real response has arrived.
  const hasFetched = systemPulse.status !== "checking";
  const bridgeState: BridgeState = systemPulse.bridgeState;
  const [activeLayer, setActiveLayer] = useState("data");
  const serviceCount = systemPulse.serviceLive;
  const totalServices = systemPulse.serviceTotal;
  // "UNAVAILABLE"/"UNKNOWN" (never a fabricated "DEGRADED"/"0") are the
  // truthful labels whenever the guardian endpoint genuinely hasn't
  // delivered a known systemHealth value yet — see systemPulse.js's
  // `extractGuardianState`.
  const guardianMode = systemPulse.guardianState.toUpperCase();
  const riskFlagCount = systemPulse.missingFields.includes("riskFlags") ? null : systemPulse.riskFlags.length;
  const universeLabel = systemPulse.universe.active !== null && systemPulse.universe.total !== null
    ? `${systemPulse.universe.active}/${systemPulse.universe.total}`
    : "—";
  const layerCopy: Record<string, { title: string; detail: string; state: ProofState }> = {
    data: { title: "Data Foundation", detail: "Preise, Marktstatus, Quellenfrische und Lineage bilden die Voraussetzung jeder Entscheidung.", state: "contract" },
    forecast: { title: "Forecast Truth", detail: "Prognosen, Due Dates und Outcomes bleiben über 24h-, 7d-, 10td- (zwei Handelswochen · 10 US-Handelssitzungen) und 30d-Horizonte getrennt.", state: "audit" },
    agents: { title: "Agent Consensus", detail: "Spezialisierte Rollen analysieren, challengen und synthetisieren – mit nachvollziehbarer Übergabe.", state: "contract" },
    capital: { title: "Capital Governor", detail: "Risiko darf eine attraktive Chance blockieren. Execution bleibt im Paper-Modus.", state: "contract" },
    proof: { title: "Evidence Ledger", detail: "Live-Daten, Audit-Snapshots, Code-Verträge und offene Beweise werden sichtbar getrennt.", state: "live" },
  };
  const selectedLayer = layerCopy[activeLayer];

  // Cards and cockpit now read the SAME seven-endpoint fetch (see
  // HqsCommandCenter's always-on `pulseWorldData`/`systemPulse`), so this no
  // longer compares two independently-fetched sources — it compares the
  // RAW per-endpoint classification (`worldSummary`, "did each of the seven
  // calls come back live") against the FIELD-VALIDATED canonical status
  // (`systemPulse.status`, "were the specific fields each card promises
  // actually usable"). A genuine HTTP-200-but-malformed-payload case (Task 5)
  // makes these disagree — 7/7 raw-live cards next to a "partial" cockpit —
  // and that is exactly the honest contradiction to surface, never resolve
  // silently.
  const diagnosticCardsAllLive = worldSummary.total > 0 && worldSummary.live === worldSummary.total;
  const hasContradiction = hasFetched && diagnosticCardsAllLive && systemPulse.status !== "live" && systemPulse.status !== "checking";

  return (
    <div className="view-world pulse-world">
      {hasContradiction ? (
        <section className="pulse-contradiction-banner" role="alert">
          <TriangleAlert />
          <div>
            <strong>Widerspruch zwischen Diagnosekarten und Hauptcockpit</strong>
            <p>
              Die Diagnosekarten melden {worldSummary.live}/{worldSummary.total} live, während der kanonische
              Systemzustand „{systemPulse.status.toUpperCase()}“ zeigt (Guardian {guardianMode}).
              {systemPulse.reason ? ` Grund: ${systemPulse.reason}` : ""}
              {systemPulse.missingFields.length > 0 ? ` Fehlende/ungültige Felder: ${systemPulse.missingFields.join(", ")}.` : ""}
              {" "}Beide Zustände stammen aus denselben sieben Endpoints — HTTP 200 allein bedeutet nicht live; der
              Widerspruch wird hier angezeigt statt automatisch aufgelöst.
            </p>
          </div>
        </section>
      ) : null}
      <section className="pulse-hero liquid-panel">
        <div className="pulse-hero__copy">
          <div className="eyebrow-row">
            <Badge className="nexus-badge"><StatusDot tone={bridgeState === "connected" ? "good" : bridgeState === "unknown" ? "neutral" : "bad"} /> HQS NEXUS</Badge>
            <span className="mono-label">EVIDENCE-NATIVE INTELLIGENCE OS</span>
          </div>
          <h1>
            {mode === "operator" ? "Operative Wahrheit." : "Eine Lage. Eine Wahrheit."}
            <span>{bridgeState === "connected" ? " System kohärent." : bridgeState === "unknown" ? " Erstprüfung läuft." : " Ein Pfad verlangt Aufmerksamkeit."}</span>
          </h1>
          <p>
            Markt, Forecasts, Agenten, Kapital und technische Produktion auf einer synchronen Entscheidungsfläche – mit sichtbarer Herkunft jeder Aussage.
          </p>
          <div className="pulse-hero__facts">
            <span><Server /> {serviceCount !== null && totalServices !== null ? `${serviceCount}/${totalServices}` : "—/—"} Services</span>
            <span><Orbit /> Universum: {universeLabel}</span>
            <span><LockKeyhole /> Paper Lock</span>
          </div>
          <div className="selected-layer">
            <div><ProofChip state={selectedLayer.state} /><span>SELECTED LAYER</span></div>
            <strong>{selectedLayer.title}</strong>
            <p>{selectedLayer.detail}</p>
          </div>
        </div>
        <NexusCore
          bridgeState={bridgeState}
          guardianMode={guardianMode}
          serviceCount={serviceCount}
          totalServices={totalServices}
          activeLayer={activeLayer}
          onLayerChange={setActiveLayer}
        />
      </section>

      <section className={`attention-ribbon attention-ribbon--${bridgeState}`}>
        <span className="attention-ribbon__icon">{bridgeState === "connected" ? <CheckCircle2 /> : <TriangleAlert />}</span>
        <div>
          <span className="section-kicker">OWNER ATTENTION · P0</span>
          <strong>{bridgeState === "connected" ? "Produktions-Read-Path antwortet" : bridgeState === "unknown" ? "Erstprüfung läuft" : "Frontend → Backend Read-Path degradiert"}</strong>
          <p>{bridgeState === "connected" ? "Summary, Readiness und Guardian sind im aktuellen Abruf verfügbar." : bridgeState === "unknown" ? "Der erste Live-Abruf ist noch nicht abgeschlossen." : (systemPulse.reason || `Summary meldet HTTP ${snapshot.summary.status || "OFF"}. Die Infrastruktur ist vorhanden, der sichere Datenpfad bleibt getrennt zu prüfen.`)}</p>
        </div>
        <div className="attention-ribbon__next">
          <span>Nächster sinnvoller Schritt</span>
          <strong>{bridgeState === "connected" ? "Live-Beweise erweitern" : bridgeState === "unknown" ? "Kurz warten" : "Upstream Auth prüfen"}</strong>
          <ArrowRight />
        </div>
      </section>

      <section className="pulse-metrics" aria-label="Kernzustände">
        <MetricTile label="Infrastructure" value={serviceCount !== null && totalServices !== null ? `${serviceCount}/${totalServices}` : "—/—"} detail="Railway operator-summary snapshot" icon={Server} tone="good" proof="audit" />
        <MetricTile label="Read Path" value={bridgeState === "connected" ? "READY" : bridgeState === "unknown" ? "PRÜFUNG" : "BLOCKED"} detail={`Readiness-Endpoint · HTTP ${snapshot.summary.status || "—"}`} icon={Network} tone={bridgeState === "connected" ? "good" : bridgeState === "unknown" ? "neutral" : "bad"} proof="live" />
        <MetricTile label="Guardian" value={guardianMode} detail={riskFlagCount !== null ? `${riskFlagCount} aktive Risk Flags` : "Risk Flags nicht geliefert"} icon={ShieldCheck} tone="warn" proof="live" />
        <MetricTile label="Capital Mode" value="PAPER" detail="Real execution intentionally locked" icon={LockKeyhole} tone="violet" proof="contract" />
      </section>

      <section className="decision-panel liquid-panel">
        <SectionHeading

          kicker="THE LIVING DECISION SPINE"
          title="Vom Marktimpuls bis zum Lernen"
          detail="Jede Stufe bleibt zeitlich, fachlich und beweisseitig getrennt."
          action={<ProofChip state="contract" />}
        />
        <DecisionSpine />
      </section>

      <section className="pulse-lower-grid">
        <AskNexus bridgeState={bridgeState} forecastQueueOpen={systemPulse.forecastQueue.open} />
        <article className="owner-queue liquid-panel">
          <SectionHeading kicker="DECISION QUEUE" title="Drei Dinge verdienen Aufmerksamkeit" action={<span className="live-label"><i /> LIVE + AUDIT</span>} />
          <div className="queue-items">
            <button type="button" className={`queue-item ${bridgeState === "connected" ? "" : "queue-item--critical"}`}>
              <span><ShieldAlert /></span>
              <div>
                <small>P0 · READ PATH</small>
                <strong>{bridgeState === "connected" ? "Read Path bestätigt" : "Summary-Brücke verifizieren"}</strong>
                <p>{bridgeState === "connected" ? "Critical Summary, Readiness und Guardian liefern einen vollständigen kanonischen Zustand." : (systemPulse.reason || "Live-Oberfläche erhält keinen vollständigen Produktionszustand.")}</p>
              </div>
              <ChevronRight />
            </button>
            <button type="button" className="queue-item queue-item--watch">
              <span><SearchCheck /></span>
              <div><small>P1 · FORECAST TRUTH</small><strong>{systemPulse.forecastQueue.open !== null ? `${systemPulse.forecastQueue.open} offene Forecasts` : "—"}</strong><p>{systemPulse.forecastQueue.open !== null ? "Due-Date und Zielhistorie vor Lernen trennen." : "Aktueller Wert nicht geliefert."}</p></div>
              <ChevronRight />
            </button>
            <button type="button" className="queue-item">
              <span><ClipboardCheck /></span>
              <div><small>P2 · BUYER PROOF</small><strong>Shadow-Ergebnis vervollständigen</strong><p>PnL, SPY-Vergleich und Drawdown bilden den Wertnachweis.</p></div>
              <ChevronRight />
            </button>
          </div>
          <div className="queue-summary"><span>HQS priorisiert Auswirkungen statt Meldungsmenge.</span><strong>3 offen · 0 automatisch ausgeführt</strong></div>
        </article>
      </section>
    </div>
  );
}

function IntelligenceView({ systemPulse }: { systemPulse: ReturnType<typeof normalizeSystemPulseResponses> }) {
  const [horizon, setHorizon] = useState("7D");
  const forecastAudit = buildForecastAudit(systemPulse.forecastQuality);
  const curve = horizon === "24H" ? "18,74 42,66 68,72 96,54 126,58 156,40 190,48 224,28 256,35 292,20" : horizon === "30D" ? "18,76 42,60 68,64 96,46 126,50 156,30 190,38 224,22 256,26 292,12" : "18,72 42,62 68,70 96,48 126,55 156,34 190,44 224,25 256,33 292,17";

  return (
    <div className="view-world intelligence-world">
      <PageIntro kicker="MARKET · FORECAST · EVIDENCE" title="Intelligence Field" description="Nicht nur Signale anzeigen: HQS macht Datenqualität, Unsicherheit, Agentenbeiträge und das spätere Ergebnis gemeinsam sichtbar." proof="audit">
        <div className="horizon-switch" role="group" aria-label="Forecast-Horizont">
          {["24H", "7D", "30D"].map((item) => <button type="button" key={item} className={horizon === item ? "active" : ""} onClick={() => setHorizon(item)}>{item}</button>)}
        </div>
      </PageIntro>

      <section className="truth-lens liquid-panel">
        <div className="truth-lens__header">
          <div>
            <span className="section-kicker">FORECAST TRUTH LENS · {horizon}</span>
            <h2>Prognose und Realität auf derselben Zeitachse</h2>
          </div>
          <div className="truth-lens__legend"><span><i className="forecast" /> Forecast range</span><span><i className="actual" /> Outcome</span></div>
        </div>
        <div className="truth-lens__body">
          <div className="truth-chart" aria-label="Illustrative Forecast- und Outcome-Kurve">
            <div className="truth-chart__grid" aria-hidden="true" />
            <svg viewBox="0 0 310 100" preserveAspectRatio="none" role="img" aria-label="Audit-Visualisierung der Forecast-Entwicklung">
              <defs>
                <linearGradient id="forecastBand" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#50e6ff" stopOpacity=".38"/><stop offset="1" stopColor="#50e6ff" stopOpacity="0"/></linearGradient>
                <linearGradient id="actualLine" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#7b61ff"/><stop offset="1" stopColor="#3dffb4"/></linearGradient>
              </defs>
              <polygon points={`${curve} 292,84 18,84`} fill="url(#forecastBand)" />
              <polyline points={curve} fill="none" stroke="#50e6ff" strokeWidth="2" strokeDasharray="3 3" />
              <polyline points="18,80 42,69 68,75 96,58 126,63 156,45 190,41 224,35 256,39 292,28" fill="none" stroke="url(#actualLine)" strokeWidth="2.8" />
            </svg>
            <span className="truth-chart__marker truth-chart__marker--forecast">FORECAST</span>
            <span className="truth-chart__marker truth-chart__marker--outcome">OUTCOME</span>
            <div className="truth-chart__axis"><span>T₀</span><span>DUE DATE</span><span>VERIFIED</span></div>
          </div>
          <div className="truth-insight">
            <ProofChip state={systemPulse.forecastQuality.accuracyPct !== null ? "live" : "pending"} />
            <span className="section-kicker">CANONICAL SNAPSHOT</span>
            <strong>{formatNullableCount(systemPulse.forecastQueue.open)}</strong>
            <p>{systemPulse.forecastQueue.open !== null ? "Aktuell offene Forecasts im Bestand (noch nicht final verifiziert)." : NOT_DELIVERED_LONG}</p>
            <div className="truth-insight__meter"><span><i style={{ width: systemPulse.forecastQuality.accuracyPct !== null ? `${systemPulse.forecastQuality.accuracyPct}%` : "0%" }} /></span><strong>{formatNullablePercent(systemPulse.forecastQuality.accuracyPct)} Accuracy</strong></div>
            <small>Keine Vermischung mit alter Agent-Accuracy.</small>
          </div>
        </div>
      </section>

      <section className="forecast-audit-grid">
        {forecastAudit.map((item, index) => (
          <article className={`forecast-audit-card forecast-audit-card--${item.tone}`} key={item.label}>
            <div><span>{String(index + 1).padStart(2, "0")}</span><ProofChip state="audit" compact /></div>
            <strong>{item.value}</strong><span>{item.label}</span><small>{item.detail}</small>
          </article>
        ))}
      </section>

      <section className="intelligence-grid">
        <article className="regime-field liquid-panel">
          <SectionHeading kicker="MARKET WORLD STATE" title="Regime Field" detail="Die Lage beeinflusst Vertrauen und Kapitalquote – nicht umgekehrt." action={<ProofChip state="contract" compact />} />
          <div className="regime-orbit">
            <div className="regime-orbit__rings"><i/><i/><i/></div>
            <div className="regime-orbit__center"><Activity/><strong>VOLATILE</strong><span>NEUTRAL RISK MODE</span></div>
            <span className="regime-satellite regime-satellite--one"><TrendingUp/> Momentum<small>MIXED</small></span>
            <span className="regime-satellite regime-satellite--two"><Database/> Data<small>n/a</small></span>
            <span className="regime-satellite regime-satellite--three"><ShieldCheck/> Trust<small>GATED</small></span>
          </div>
        </article>
        <article className="lineage-stack liquid-panel">
          <SectionHeading kicker="EVIDENCE CHAIN" title="Aus Aussage wird Beweis" detail="Jeder Layer zeigt Quelle, Zustand und Verantwortlichkeit." />
          <div className="lineage-items">
            <div><span><Database/></span><strong>Market snapshot</strong><small>Quelle · Zeitstempel · Frische</small><ProofChip state="live" compact /></div>
            <div><span><Binary/></span><strong>Feature & Score</strong><small>Version · Faktor · Transformation</small><ProofChip state="contract" compact /></div>
            <div><span><BrainCircuit/></span><strong>Agent contribution</strong><small>Modell · Confidence · Dissens</small><ProofChip state="pending" compact /></div>
            <div><span><Fingerprint/></span><strong>Outcome evidence</strong><small>Due Date · Target · Verification</small><ProofChip state="audit" compact /></div>
          </div>
        </article>
      </section>
    </div>
  );
}

function CapitalView({ capitalPulse }: { capitalPulse: ReturnType<typeof normalizeCapitalPulse> }) {
  const [vixShock, setVixShock] = useState([28]);
  const [correlation, setCorrelation] = useState([46]);
  const stress = Math.min(100, Math.round(vixShock[0] * 1.35 + correlation[0] * 0.68));

  return (
    <div className="view-world capital-world">
      <PageIntro kicker="PORTFOLIO · RISK · SCENARIO" title="Capital Field" description="Eine räumliche Risikolandschaft zeigt nicht nur Positionen, sondern auch Vertrauen, Korrelation, Liquidität und bewusstes Nicht-Handeln." proof="contract">
        <div className="paper-lock"><LockKeyhole/><span>EXECUTION MODE</span><strong>PAPER ONLY</strong></div>
      </PageIntro>

      <section className="capital-stage liquid-panel">
        <div className="capital-stage__field">
          <div className="capital-field-grid" aria-hidden="true" />
          <div className="capital-shield capital-shield--outer" aria-hidden="true" />
          <div className="capital-shield capital-shield--inner" aria-hidden="true" />
          <div className="capital-center">
            <span>RISK BUDGET</span><strong>{100 - stress}%</strong><small>SIMULATED CAPACITY</small>
          </div>
          <button type="button" className="capital-node capital-node--data"><Database/><strong>DATA</strong><span>TRUST GATE</span></button>
          <button type="button" className="capital-node capital-node--forecast"><Radar/><strong>EDGE</strong><span>FORECAST</span></button>
          <button type="button" className="capital-node capital-node--regime"><Activity/><strong>REGIME</strong><span>VOLATILE</span></button>
          <button type="button" className="capital-node capital-node--correlation"><Network/><strong>CORR</strong><span>{correlation[0]}%</span></button>
          <button type="button" className="capital-node capital-node--cash"><ShieldCheck/><strong>CASH</strong><span>BUFFER</span></button>
          <div className="capital-field__caption"><CircleDot/> Interface simulation · keine Order und keine Backend-Mutation</div>
        </div>
        <div className="scenario-console">
          <div className="scenario-console__heading"><span><FlaskConical/></span><div><span className="section-kicker">COUNTERFACTUAL LAB</span><h2>Stress-Szenario</h2></div><ProofChip state="contract" compact /></div>
          <p>Bewege die Parameter und prüfe, wie sich das verfügbare Risikobudget in der Oberfläche verändert.</p>
          <div className="scenario-control">
            <div><span>VIX-Schock</span><strong>+{vixShock[0]} %</strong></div>
            <Slider value={vixShock} onValueChange={setVixShock} max={60} step={1} aria-label="VIX-Schock" />
          </div>
          <div className="scenario-control">
            <div><span>Korrelationsanstieg</span><strong>{correlation[0]} %</strong></div>
            <Slider value={correlation} onValueChange={setCorrelation} max={100} step={1} aria-label="Korrelationsanstieg" />
          </div>
          <div className={`stress-result ${stress > 70 ? "stress-result--critical" : ""}`}>
            <div><span>STRESS LOAD</span><strong>{stress}%</strong></div>
            <Progress value={stress} />
            <p>{stress > 70 ? "PORTFOLIO_RISK_FULL würde neue Positionen blockieren." : "Risikokapazität bleibt im simulierten Bereich vorhanden."}</p>
          </div>
        </div>
      </section>

      <section className="capital-lower-grid">
        <article className="governor-stack liquid-panel">
          <SectionHeading kicker="CAPITAL GOVERNOR" title="Fünf gültige Gründe, nichts zu tun" detail="Nicht-Handeln ist ein dokumentiertes Systemergebnis." />
          <div className="governor-reasons">
            {["NO_EDGE", "INSUFFICIENT_TRUST", "MARKET_UNFAVORABLE", "PORTFOLIO_RISK_FULL", "DATA_BLOCKED"].map((reason, index) => <div key={reason}><span>{String(index + 1).padStart(2, "0")}</span><strong>{reason}</strong><StatusDot tone={index === 1 ? "warn" : "neutral"}/></div>)}
          </div>
        </article>
        <article className="portfolio-contract liquid-panel">
          <SectionHeading kicker="PORTFOLIO TWIN" title="Eigenständiger Portfolio-Bereich aktiv" detail="Gesamtübersicht, Positionen, Kandidaten, Watchlist, Verkäufe, Journal und Einzeltitel-Dossiers sind getrennt erreichbar." action={<ProofChip state="contract" compact />} />
          {/* fix/pnl-dossier-mapping-and-live-truth: ACTIVE used to be a
             static "8" captioned "Illustrativer Paper-Snapshot" — the real
             openCount was already fetched by the "capital" world's own
             portfolio-twin endpoint, just never reaching this view.
             CANDIDATES/JOURNAL have no verified equivalent anywhere in the
             capital world's endpoint set (saved-capital, near-misses,
             guardian, portfolio-twin, virtual-positions, interface-state)
             — shown as genuinely not delivered rather than a guessed count. */}
          <div className="portfolio-contract__visual">
            <div><span>ACTIVE</span><strong>{capitalPulse.openCount ?? NOT_DELIVERED}</strong><small>{capitalPulse.openCount !== null ? "Live aus Portfolio-Twin" : NOT_DELIVERED_LONG}</small></div>
            <div><span>CANDIDATES</span><strong>{NOT_DELIVERED}</strong><small>{NOT_DELIVERED_LONG}: kein Live-Endpoint für eine Kandidaten-Queue.</small></div>
            <div><span>JOURNAL</span><strong>{NOT_DELIVERED}</strong><small>{NOT_DELIVERED_LONG}: kein Live-Endpoint für ein Entscheidungsjournal.</small></div>
          </div>
          <div className="contract-notice"><CheckCircle2/> Der vollständige Portfolio-UI-Vertrag ist umgesetzt. Die Werte bleiben als Paper-Snapshot markiert, bis der geschützte Live-Read-Path angeschlossen ist.</div>
        </article>
      </section>
    </div>
  );
}

function AgentsView() {
  return <Suspense fallback={<div className="nexus-world-loading"><BrainCircuit/><strong>Agent Mesh wird verbunden</strong><span>Chats, Tasks und Providerstatus werden geladen …</span></div>}><NexusAgentCommunicationHub /></Suspense>;
}

function OperationsView({ snapshot, mode, systemPulse, operationsPulse }: { snapshot: LiveSnapshot; mode: ExperienceMode; systemPulse: ReturnType<typeof normalizeSystemPulseResponses>; operationsPulse: ReturnType<typeof normalizeOperationsPulse> }) {
  // fix/system-pulse-security-and-live-truth: bridgeConnected/service counts/
  // readiness used to read the old, never-truthfully-populated /api/hqs-live
  // snapshot fields (`readiness.data.ready`, `infrastructure.railway
  // .successfulServices/totalServices`). This view now reads the SAME
  // canonical systemPulse object the Pulse cockpit reads, so every surface
  // agrees on one truth.
  const bridgeConnected = systemPulse.bridgeState === "connected";
  const [selectedStage, setSelectedStage] = useState(4);
  const stage = PIPELINE_STAGES[selectedStage];
  // fix/pnl-dossier-mapping-and-live-truth: the pipeline steps below and the
  // Postgres capacity node used to render a fixed "—"/"NO LIVE CAPACITY"
  // string with no live source — /api/admin/pipeline-status and
  // /api/admin/db-capacity were already being fetched by the "operations"
  // world (see operationsPulse.js), just never mapped into this view.
  const stageKeys = Object.keys(PIPELINE_STAGE_KEY_CANDIDATES);
  const selectedStageLive = operationsPulse.pipeline.stages[stageKeys[selectedStage]] ?? null;
  const dbCapacity = operationsPulse.dbCapacity;
  const dbCapacityLabel = dbCapacity?.severity === "critical" ? "KRITISCH"
    : dbCapacity?.severity === "warning" ? "WARNUNG"
    : dbCapacity?.severity === "ok" ? "OK"
    : NOT_DELIVERED;
  const dbCapacityBad = dbCapacity?.severity === "critical";

  return (
    <div className="view-world operations-world">
      <PageIntro kicker="PIPELINE · PROVIDERS · INFRASTRUCTURE" title="Operations Twin" description="Der digitale Zwilling verbindet Produktionskette, Services, Datenbank, Provider und Read-Models auf einer gemeinsamen Zeitachse." proof="audit">
        <div className="ops-mode"><span><StatusDot tone={bridgeConnected ? "good" : "bad"}/> OBSERVABILITY</span><strong>{mode === "operator" ? "DEEP" : "OWNER"}</strong></div>
      </PageIntro>

      <section className="pipeline-twin liquid-panel">
        <div className="pipeline-twin__beam" aria-hidden="true"><i/></div>
        {PIPELINE_STAGES.map((item, index) => {
          const live = operationsPulse.pipeline.stages[stageKeys[index]] ?? null;
          const stageLabel = live
            ? (live.successCount !== null && live.inputCount !== null ? `${live.successCount}/${live.inputCount}` : (live.status ?? NOT_DELIVERED).toUpperCase())
            : NOT_DELIVERED;
          return (
            <button type="button" key={item.name} className={`pipeline-stage pipeline-stage--${item.state} ${selectedStage === index ? "active" : ""}`} onClick={() => setSelectedStage(index)}>
              <span>{String(index + 1).padStart(2, "0")}</span><i><CircleDot/></i><strong>{item.short}</strong><small>{stageLabel}</small>
            </button>
          );
        })}
      </section>

      <section className="operations-grid">
        <article className="stage-inspector liquid-panel">
          <div className="stage-inspector__heading"><span>{String(selectedStage + 1).padStart(2, "0")}</span><div><span className="section-kicker">SELECTED PIPELINE STAGE</span><h2>{stage.name}</h2><p>{stage.detail}</p></div><ProofChip state={selectedStageLive ? "live" : "pending"} compact /></div>
          {/* fix/pnl-dossier-mapping-and-live-truth: /api/admin/pipeline-status
             reports real per-stage success/input/failed counts (never a
             computed "audit readiness" percentage) — shown as that real
             fraction now instead of a fabricated or static "—". */}
          <div className="stage-inspector__progress"><div><span>Erfolgreich / Eingelesen</span><strong>{selectedStageLive && selectedStageLive.successCount !== null && selectedStageLive.inputCount !== null ? `${selectedStageLive.successCount} / ${selectedStageLive.inputCount}` : NOT_DELIVERED}</strong></div></div>
          <div className="stage-inspector__facts"><div><span>Dependency</span><strong>{selectedStage === 0 ? "Market providers" : PIPELINE_STAGES[selectedStage - 1].name}</strong></div><div><span>Downstream</span><strong>{selectedStage === PIPELINE_STAGES.length - 1 ? "Evidence ledger" : PIPELINE_STAGES[selectedStage + 1].name}</strong></div><div><span>Runtime controls</span><strong>READ ONLY</strong></div></div>
          {selectedStageLive
            ? <div className="contract-notice"><Info/> Live aus /api/admin/pipeline-status ({selectedStageLive.stageKey}) · Status: {selectedStageLive.status ?? NOT_DELIVERED}{selectedStageLive.lastUpdated ? ` · ${formatDateTime(selectedStageLive.lastUpdated)}` : ""}.</div>
            : <div className="contract-notice"><Info/> {NOT_DELIVERED_LONG}: kein passender Pipeline-Status-Eintrag für diese Stufe geliefert.</div>}
        </article>

        <article className="system-twin liquid-panel">
          <SectionHeading kicker="LIVE SYSTEM TOPOLOGY" title="Read-Path Twin" detail="Frontend, API, Railway und Datenbank werden als ein Pfad lesbar." action={<ProofChip state="live" compact />} />
          <div className="twin-route">
            <div className="twin-node"><Cloud/><strong>VERCEL</strong><span>READY</span></div><i className="twin-link twin-link--good"><ArrowRight/></i>
            <div className={`twin-node ${bridgeConnected ? "" : "twin-node--bad"}`}><Network/><strong>READ BRIDGE</strong><span>HTTP {snapshot.summary.status || "OFF"}</span></div><i className={`twin-link ${bridgeConnected ? "twin-link--good" : "twin-link--bad"}`}><ArrowRight/></i>
            <div className="twin-node"><Server/><strong>RAILWAY</strong><span>{formatNullableCount(systemPulse.serviceLive, systemPulse.serviceTotal)}</span></div><i className="twin-link twin-link--neutral"><ArrowRight/></i>
            {/* fix/pnl-dossier-mapping-and-live-truth: /api/admin/db-capacity
               is already fetched by the "operations" world — its real
               severity/vacuum/index summary is used here instead of the
               previous static "NO LIVE CAPACITY" literal. */}
            <div className={`twin-node ${dbCapacityBad ? "twin-node--bad" : ""}`}><Database/><strong>POSTGRES</strong><span>{dbCapacityLabel}</span></div>
          </div>
          <div className="twin-statuses"><div><span>Frontend summary</span><strong className={snapshot.summary.ok ? "good" : "bad"}>{snapshot.summary.ok ? "CONNECTED" : `HTTP ${snapshot.summary.status || "OFF"}`}</strong></div><div><span>Readiness</span><strong className={systemPulse.readPathState === "ready" ? "good" : "warn"}>{systemPulse.readPathState === "ready" ? "READY" : "NOT READY"}</strong></div><div><span>Runtime memory</span><strong>{formatNullableDecimal(snapshot.infrastructure.railway.memoryGb, 3, " GB")}</strong></div></div>
        </article>
      </section>

      <section className="ops-detail-grid">
        <article className="provider-plane liquid-panel">
          <SectionHeading kicker="DATA FOUNDATION" title="Provider & Freshness Plane" detail="Jede Quelle benötigt Verfügbarkeit, Frische, Coverage und Fallback." action={<ProofChip state="pending" compact />} />
          {[{name:"Massive",role:"Primary US price feed",state:"Read model pending"},{name:"FMP",role:"Active market provider",state:"Read model pending"},{name:"Finnhub",role:"Retained fallback",state:"Read model pending"},{name:"Twelve Data",role:"Replacement provider",state:"Read model pending"}].map((provider) => <div className="provider-row" key={provider.name}><span><Database/></span><div><strong>{provider.name}</strong><small>{provider.role}</small></div><em>{provider.state}</em><ProofChip state="pending" compact /></div>)}
        </article>
        <article className="state-timeline liquid-panel">
          <SectionHeading kicker="STATE TIMELINE" title="Zustandswechsel statt Momentaufnahme" detail="Die finale Anbindung macht Dauer und Reihenfolge von Fehlern sichtbar." />
          {/* fix/system-pulse-security-and-live-truth: this used to render
             fixed 72/28, 46/54, 24/76 percent splits with no backing
             history endpoint at all — a fabricated-looking timeline chart.
             No live history source is connected yet, so it is shown as
             genuinely not delivered instead of another invented split. */}
          <div className="timeline-axis"><span>00:00</span><span>06:00</span><span>12:00</span><span>NOW</span></div>
          <div className="timeline-row"><span>PIPELINE</span><div><i className="neutral" style={{width:"100%"}}/></div></div>
          <div className="timeline-row"><span>READ PATH</span><div><i className="neutral" style={{width:"100%"}}/></div></div>
          <div className="timeline-row"><span>GUARDIAN</span><div><i className="neutral" style={{width:"100%"}}/></div></div>
          <small className="timeline-caption">{NOT_DELIVERED_LONG}: echte historische Events benötigen den Audit-Read-Path.</small>
        </article>
      </section>
    </div>
  );
}

function LearningView({ systemPulse, learningPulse }: { systemPulse: ReturnType<typeof normalizeSystemPulseResponses>; learningPulse: ReturnType<typeof normalizeLearningPulse> }) {
  const [mode, setMode] = useState("Canonical");
  const accuracyPct = systemPulse.forecastQuality.accuracyPct;
  // fix/pnl-dossier-mapping-and-live-truth: /api/admin/learning-diagnostics
  // was already fetched by the "learning" world but never parsed into this
  // view — real learningUpdates/memoryUpdates/featureHistory/discoveryLabels
  // counts now replace the total silence that existed here before (this is
  // ADDITIVE to the honestly-disclosed "rekonstruierbar/Target-Historie/ohne
  // Due Date" breakdown below, which still has no matching live endpoint).
  const diagnostics = learningPulse.diagnostics;
  return (
    <div className="view-world learning-world">
      <PageIntro kicker="OUTCOME · WEIGHTS · CAUSAL MEMORY" title="Learning Ledger" description="HQS darf nur aus belegten Outcomes lernen. Alte Accuracy, Shadow-Empfehlungen und produktive Gewichtung bleiben getrennte Ebenen." proof="audit">
        <div className="learning-mode">{["Canonical","Shadow","History"].map((item) => <button type="button" key={item} className={mode === item ? "active" : ""} onClick={() => setMode(item)}>{item}</button>)}</div>
      </PageIntro>

      <section className="learning-hero liquid-panel">
        <div className="learning-score">
          <div className="learning-score__rings"><i/><i/><i/></div>
          <span>CANONICAL</span><strong>{formatNullablePercent(accuracyPct)}</strong><small>POST-CUTOVER ACCURACY</small>
        </div>
        <div className="learning-hero__copy">
          <ProofChip state={accuracyPct !== null ? "live" : "pending"} />
          <span className="section-kicker">{mode.toUpperCase()} VIEW</span>
          <h2>Das System lernt nur aus überprüfbarer Wahrheit.</h2>
          <p>Forecast, Due Date, Zielhistorie, Outcome und Gewichtsempfehlung müssen dieselbe Lineage tragen. Fehlt ein Glied, bleibt die Empfehlung im Shadow-Modus.</p>
          {/* fix/system-pulse-security-and-live-truth: the previous static
             "rekonstruierbar / Target-Historie fehlt / ohne Due Date"
             breakdown (three specific fixed counts) has no connected
             backend source at all — no endpoint reports these three
             specific categories — so it is shown as genuinely not
             delivered rather than estimated. */}
          <div className="learning-facts"><span><strong>{NOT_DELIVERED}</strong> rekonstruierbar</span><span><strong>{NOT_DELIVERED}</strong> Target-Historie fehlt</span><span><strong>{NOT_DELIVERED}</strong> ohne Due Date</span></div>
          <small className="timeline-caption">{NOT_DELIVERED_LONG}: Aufschlüsselung nach Rekonstruierbarkeit ist noch nicht an einen Live-Endpoint angebunden.</small>
          <div className="learning-facts">
            <span><strong>{diagnostics?.learningUpdates ?? NOT_DELIVERED}</strong> Learning-Updates</span>
            <span><strong>{diagnostics?.memoryUpdates ?? NOT_DELIVERED}</strong> Memory-Updates</span>
            <span><strong>{diagnostics?.featureHistory?.rowsLast24h ?? NOT_DELIVERED}</strong> Feature-History (24h)</span>
            <span><strong>{diagnostics?.discoveryLabels?.totalRows ?? NOT_DELIVERED}</strong> Discovery Labels</span>
            <span><strong>{diagnostics?.outcomeTrackingReadiness?.totalRows ?? NOT_DELIVERED}</strong> Outcome-Zeilen gesamt</span>
          </div>
          <small className="timeline-caption">
            {diagnostics
              ? `Live aus /api/admin/learning-diagnostics${diagnostics.message ? ` · ${diagnostics.message}` : ""}`
              : `${NOT_DELIVERED_LONG}: /api/admin/learning-diagnostics derzeit nicht erreichbar.`}
          </small>
        </div>
      </section>

      <section className="learning-grid">
        <article className="learning-flow liquid-panel">
          <SectionHeading kicker="GOVERNED WEIGHT LEARNING" title="Empfehlung vor Mutation" detail="Jede Änderung durchläuft Simulation, Shadow-Vergleich und Freigabe." action={<ProofChip state="contract" compact />} />
          <div className="learning-steps">
            <div><span><SearchCheck/></span><strong>Outcome verified</strong><small>Canonical source</small><em>01</em></div>
            <div><span><Binary/></span><strong>Recommendation</strong><small>No direct write</small><em>02</em></div>
            <div><span><FlaskConical/></span><strong>Shadow evaluation</strong><small>Compare & stress</small><em>03</em></div>
            <div><span><UserCheck/></span><strong>Owner approval</strong><small>Governed promotion</small><em>04</em></div>
            <div><span><History/></span><strong>Rollback ready</strong><small>Versioned weights</small><em>05</em></div>
          </div>
        </article>
        <article className="learning-risks liquid-panel">
          <SectionHeading kicker="KNOWN STRUCTURAL RISKS" title="Was Lernen verfälschen kann" detail="Offene Risiken werden nicht hinter einem Gesamtscore versteckt." />
          <div className="risk-card risk-card--bad"><span><ShieldAlert/></span><div><strong>Mehrfach-Writer</strong><p>Kern-Tabellen besitzen dokumentierte Konfliktzonen.</p></div><ProofChip state="audit" compact /></div>
          <div className="risk-card risk-card--warn"><span><GitBranch/></span><div><strong>Accuracy-Migration</strong><p>Lokale Berechnungen sind noch nicht vollständig zentralisiert.</p></div><ProofChip state="audit" compact /></div>
          <div className="risk-card"><span><Timer/></span><div><strong>Runtime-Nachweis</strong><p>Produktive Agentenläufe benötigen durchgehende Traces.</p></div><ProofChip state="pending" compact /></div>
        </article>
      </section>
    </div>
  );
}

// fix/pnl-dossier-mapping-and-live-truth, Priority 7 ("Proof Score
// fail-closed berechnen"): /api/admin/performance-proof's own proofScore
// field is not trustworthy when no verified evidence backs it — see
// proofPulse.js's header comment for the exact backend arithmetic bug (a
// second, erroneous `* 100` that pushes proofScore to ~100 for almost any
// non-empty decision set, even with zero verified outcomes — the exact
// reported incident: 50 decisions, 0 verified, 50 pending -> proofScore
// 100). This frontend cannot repair that backend arithmetic, so it fails
// closed instead: the evidence row is only marked "live" (proven) when
// verifiedOutcomes is genuinely greater than zero; otherwise the raw score
// is shown with an explicit, unmissable caveat — never presented as
// trustworthy live proof.
function buildPerformanceProofEvidenceRow(performanceProof: ReturnType<typeof normalizeProofPulse>["performanceProof"], liveTimestamp: string): { title: string; detail: string; state: ProofState; time: string } {
  const title = "Portfolio Outcome Proof";
  if (!performanceProof || performanceProof.totalDecisions === null) {
    return { title, detail: "PnL, SPY comparison and drawdown", state: "pending", time: "OPEN" };
  }
  const { totalDecisions, verifiedOutcomes, pendingOutcomes, proofScore, hasVerifiedEvidence } = performanceProof;
  const scoreText = proofScore !== null ? `Proof Score ${proofScore.toLocaleString("de-DE")}` : "Proof Score nicht geliefert";
  if (hasVerifiedEvidence) {
    return {
      title,
      detail: `${verifiedOutcomes} verifiziert / ${pendingOutcomes ?? 0} offen von ${totalDecisions} Entscheidungen · ${scoreText}`,
      state: "live",
      time: liveTimestamp,
    };
  }
  return {
    title,
    detail: `0 verifizierte Outcomes von ${totalDecisions} Entscheidungen (${pendingOutcomes ?? totalDecisions} offen) — ${scoreText} ist NICHT belastbar ohne verifizierte Outcomes.`,
    state: "pending",
    time: liveTimestamp,
  };
}

function ProofView({ snapshot, systemPulse, proofPulse }: { snapshot: LiveSnapshot; systemPulse: ReturnType<typeof normalizeSystemPulseResponses>; proofPulse: ReturnType<typeof normalizeProofPulse> }) {
  // fix/system-pulse-security-and-live-truth: this used to read the old
  // `readiness.ready`/`infrastructure.railway.successfulServices` fields
  // (never truthfully populated) and hardcoded two fixed calendar-day
  // labels as if they were real collection timestamps for a static
  // accuracy-percentage literal. Every evidence row below now reads the
  // same canonical systemPulse
  // object as the rest of the cockpit, and a historical figure only carries
  // a date when systemPulse actually supplies one (`systemPulse.updatedAt`)
  // — never an invented date next to an invented number.
  const isReady = systemPulse.readPathState === "ready";
  const liveTimestamp = formatDateTime(systemPulse.updatedAt || undefined);
  const [filter, setFilter] = useState<ProofState | "all">("all");
  const evidence = [
    // fix/pnl-dossier-mapping-and-live-truth: both rows below used to hard-
    // code state:"live" regardless of what their own detail text said —
    // Proof & Dossier could show "Produktions-Read-Path bereit · LIVE" at
    // the exact same moment System Pulse correctly reported readPathState
    // "blocked" and Buyer Briefing showed "degradiert". The evidence-row
    // proof state must track the same canonical systemPulse/snapshot facts
    // the detail text already reads, never a separate hardcoded claim.
    { title: "Frontend Summary", detail: `/api/frontend-critical-summary · HTTP ${snapshot.summary.status || "OFF"}`, state: snapshot.summary.ok ? "live" as ProofState : "pending" as ProofState, time: formatTime(snapshot.generatedAt) },
    { title: "Readiness Gate", detail: isReady ? "Produktions-Read-Path bereit" : "Read-Path nicht vollständig bereit", state: isReady ? "live" as ProofState : "pending" as ProofState, time: formatTime(snapshot.generatedAt) },
    { title: "Infrastructure Snapshot", detail: `${formatNullableCount(systemPulse.serviceLive, systemPulse.serviceTotal)} Railway Services`, state: systemPulse.serviceLive !== null ? "live" as ProofState : "pending" as ProofState, time: systemPulse.serviceLive !== null ? liveTimestamp : "OPEN" },
    { title: "Canonical Accuracy", detail: systemPulse.forecastQuality.accuracyPct !== null ? `${formatNullablePercent(systemPulse.forecastQuality.accuracyPct)} · live aus Operator Summary` : NOT_DELIVERED_LONG, state: systemPulse.forecastQuality.accuracyPct !== null ? "live" as ProofState : "pending" as ProofState, time: systemPulse.forecastQuality.accuracyPct !== null ? liveTimestamp : "OPEN" },
    { title: "Capital Governor", detail: "Paper-only, risk-first, no-action states", state: "contract" as ProofState, time: "CODE" },
    { title: "Agent Runtime Trace", detail: "Tool calls, handoffs, cost and approvals", state: "pending" as ProofState, time: "OPEN" },
    buildPerformanceProofEvidenceRow(proofPulse.performanceProof, liveTimestamp),
  ];
  const visibleEvidence = evidence.filter((item) => filter === "all" || item.state === filter);

  return (
    <div className="view-world proof-world">
      <PageIntro kicker="LINEAGE · AUDIT · DUE DILIGENCE" title="Proof & Dossier" description="Jede Aussage trägt ihren Beweiszustand. Käufer und Besitzer erkennen sofort, was live, geprüft, im Code belegt oder noch offen ist." proof="live">
        <div className="proof-score"><Fingerprint/><div><span>EVIDENCE DISCIPLINE</span><strong>4 STATES</strong></div></div>
      </PageIntro>

      <section className="proof-legend">
        {PROOF_LEGEND.map((item) => <button type="button" key={item.state} className={`${filter === item.state ? "active" : ""} proof-legend__item proof-legend__item--${item.state}`} onClick={() => setFilter(filter === item.state ? "all" : item.state)}><ProofChip state={item.state}/><strong>{item.label}</strong><small>{item.detail}</small></button>)}
      </section>

      <section className="proof-grid">
        <article className="evidence-ledger liquid-panel">
          <SectionHeading kicker="EVIDENCE LEDGER" title={filter === "all" ? "Alle Beweiszustände" : `${filter.toUpperCase()} gefiltert`} detail="Ein Klick auf einen Status filtert die Ereigniskette." />
          <div className="evidence-list">
            {visibleEvidence.map((item, index) => <div className="evidence-row" key={item.title}><span className="evidence-row__index">{String(index + 1).padStart(2,"0")}</span><span className="evidence-row__icon">{item.state === "live" ? <Activity/> : item.state === "audit" ? <ClipboardCheck/> : item.state === "contract" ? <BookOpenCheck/> : <Timer/>}</span><div><strong>{item.title}</strong><small>{item.detail}</small></div><ProofChip state={item.state} compact/><time>{item.time}</time><ChevronRight/></div>)}
          </div>
        </article>
        <article className="dossier-stack liquid-panel">
          <SectionHeading kicker="BUYER DUE DILIGENCE" title="Das Dossier beantwortet vier Fragen" detail="Technischer Wert und offene Risiken bleiben gleichzeitig sichtbar." />
          <div className="dossier-question"><span>01</span><div><strong>Was ist wirklich implementiert?</strong><p>Architektur, Jobs, Services, Engines und Sicherheitsverträge.</p></div><CheckCircle2/></div>
          <div className="dossier-question"><span>02</span><div><strong>Was läuft nachweisbar?</strong><p>Live-Read-Modelle und aktuelle Zustände mit Zeitstempel.</p></div><Activity/></div>
          <div className="dossier-question"><span>03</span><div><strong>Was erzeugt wirtschaftlichen Wert?</strong><p>Forecast-Verifikation, Risiko-Governance und Lernfähigkeit.</p></div><TrendingUp/></div>
          <div className="dossier-question"><span>04</span><div><strong>Welche Risiken bleiben offen?</strong><p>Datenbrücke, Runtime-Proof und vollständiger Portfolio-Nachweis.</p></div><AlertTriangle/></div>
        </article>
      </section>

      <section className="verdict-panel liquid-panel">
        <div className="verdict-panel__core"><div><Target/><strong>HQS</strong><span>VERDICT</span></div><i/><i/><i/></div>
        <div className="verdict-panel__copy"><ProofChip state="audit"/><h2>Substanz sichtbar.<br/><span>Beweiskette noch nicht vollständig live.</span></h2><p>HQS NEXUS trennt Designwirkung von technischer Wahrheit. Der höchste Werthebel bleibt die vollständige Backend-Anbindung der Detail-Read-Models und ein sauberer Shadow-Nachweis.</p></div>
        <div className="verdict-panel__facts"><span><CheckCircle2/> Daily HQS Spine</span><span><CheckCircle2/> Forecast Contract</span><span><CheckCircle2/> Capital Governance</span><span><TriangleAlert/> Runtime Evidence</span></div>
      </section>
    </div>
  );
}

function BuyerExperience({ snapshot, onEnterOwner, systemPulse }: { snapshot: LiveSnapshot; onEnterOwner: () => void; systemPulse: ReturnType<typeof normalizeSystemPulseResponses> }) {
  // fix/system-pulse-security-and-live-truth: `connected` used to read the
  // old `readiness.data.ready` field, which never existed on the real
  // backend contract — the buyer briefing could never truthfully show
  // "bereit". It now reads the same canonical systemPulse.bridgeState every
  // other surface uses.
  const connected = systemPulse.bridgeState === "connected";
  const universeAvailable = systemPulse.universe.active !== null && systemPulse.universe.total !== null;

  return (
    <div className="buyer-world">
      <section id="buyer-nexus" className="buyer-hero liquid-panel">
        <div className="buyer-hero__copy">
          <Badge className="buyer-badge"><Eye/> BUYER INTELLIGENCE BRIEFING</Badge>
          <h1>Sehen, wie ein Quant-System <span>beobachtet, entscheidet und lernt.</span></h1>
          <p>HQS verbindet Datenpipeline, verifizierbare Forecasts, spezialisierte KI-Agenten und Kapital-Governance in einer nachvollziehbaren Entscheidungskette.</p>
          <div className="buyer-hero__actions"><Button onClick={onEnterOwner}><Gauge/> Owner Cockpit öffnen</Button><button type="button" onClick={() => document.getElementById("buyer-proof")?.scrollIntoView({behavior:"smooth"})}>Beweise ansehen <ArrowRight/></button></div>
          <div className="buyer-hero__trust"><span><StatusDot tone={systemPulse.serviceLive !== null ? "good" : "neutral"}/> {formatNullableCount(systemPulse.serviceLive, systemPulse.serviceTotal)} Infrastruktur-Snapshot</span><span><StatusDot tone={connected ? "good" : "bad"}/> Read-Path {connected ? "bereit" : "degradiert"}</span><span><LockKeyhole/> Paper-only</span></div>
        </div>
        <div className="buyer-nexus-visual" aria-label="HQS Wertschöpfungskern">
          <div className="buyer-nexus-visual__grid"/><i className="buyer-ring buyer-ring--one"/><i className="buyer-ring buyer-ring--two"/><i className="buyer-ring buyer-ring--three"/>
          <div className="buyer-nexus-visual__center"><Orbit/><strong>HQS</strong><span>DECISION INTELLIGENCE</span></div>
          <span className="buyer-value-node buyer-value-node--data"><Database/><strong>DATA</strong><small>FOUNDATION</small></span>
          <span className="buyer-value-node buyer-value-node--forecast"><Radar/><strong>FORECAST</strong><small>TRUTH</small></span>
          <span className="buyer-value-node buyer-value-node--agents"><BrainCircuit/><strong>AGENTS</strong><small>CHALLENGE</small></span>
          <span className="buyer-value-node buyer-value-node--capital"><Scale/><strong>CAPITAL</strong><small>GOVERN</small></span>
          <span className="buyer-value-node buyer-value-node--proof"><Fingerprint/><strong>PROOF</strong><small>LEARN</small></span>
        </div>
      </section>

      <section id="buyer-decision" className="buyer-section">
        <div className="buyer-section__intro"><span className="section-kicker">01 · THE DECISION SPINE</span><h2>Vom Rohdatum bis zum belegten Ergebnis.</h2><p>Ein Käufer muss nicht raten, wo Intelligenz endet und Risiko-Governance beginnt.</p></div>
        <div className="buyer-decision-panel liquid-panel"><DecisionSpine/></div>
      </section>

      <section id="buyer-proof" className="buyer-section">
        <div className="buyer-section__intro"><span className="section-kicker">02 · EVIDENCE BEFORE CLAIMS</span><h2>Jede Zahl zeigt, wie sicher sie ist.</h2><p>HQS NEXUS unterscheidet vier Wahrheitsklassen sichtbar und dauerhaft.</p></div>
        <div className="buyer-proof-grid">{PROOF_LEGEND.map((item, index) => <article className={`buyer-proof-card buyer-proof-card--${item.state}`} key={item.state}><span>{String(index+1).padStart(2,"0")}</span><ProofChip state={item.state}/><h3>{item.label}</h3><p>{item.detail}</p></article>)}</div>
      </section>

      <section id="buyer-dossier" className="buyer-section buyer-dossier-section">
        <div className="buyer-section__intro"><span className="section-kicker">03 · DUE DILIGENCE WITHOUT THEATER</span><h2>Wert, Reife und offene Risiken auf einen Blick.</h2><p>Das System zeigt seine Stärke gerade dadurch, dass es fehlende Laufzeitbeweise nicht versteckt.</p></div>
        <div className="buyer-value-grid">
          <article><Database/><span>DATA FOUNDATION</span><strong>{universeAvailable ? `Aktives Universum: ${formatNullableCount(systemPulse.universe.active, systemPulse.universe.total)}` : "Aktives Universum: nicht eindeutig verfügbar"}</strong><p>Mehrstufige Preis-, Qualitäts- und Lineage-Architektur.</p><ProofChip state={universeAvailable ? "live" : "contract"} compact/></article>
          <article><SearchCheck/><span>FORECAST TRUTH</span><strong>Historischer Forecastbestand – nicht als aktueller Performancebeweis verwendbar</strong><p>Cutover-Nachweis nicht verfügbar.</p><ProofChip state="pending" compact/></article>
          <article><ShieldCheck/><span>CAPITAL SAFETY</span><strong>Risk before return</strong><p>Nicht-Handeln ist ein gültiges, dokumentiertes Ergebnis.</p><ProofChip state="contract" compact/></article>
          <article><BrainCircuit/><span>MULTI-AGENT</span><strong>6 Spezialrollen</strong><p>Analyse, Gegenprüfung und Synthese werden getrennt.</p><ProofChip state="pending" compact/></article>
        </div>
        <div className="buyer-final-verdict liquid-panel"><div className="buyer-final-verdict__mark"><Target/><strong>HQS</strong><span>CURRENT MATURITY</span></div><div><ProofChip state="audit"/><h2>Technisch substanziell.<br/><span>Live-Beweis gezielt ausbaubar.</span></h2><p>Die Architektur ist deutlich größer als die heutige Live-Anbindung. NEXUS macht genau diese Lücke sichtbar und verwandelt sie in eine klare Ausbau-Roadmap.</p></div><Button onClick={onEnterOwner}>Systemtiefe öffnen <ArrowUpRight/></Button></div>
      </section>
    </div>
  );
}

function ActiveView({ activeView, snapshot, mode, worldSummary, systemPulse, operationsPulse, learningPulse, proofPulse, capitalPulse }: { activeView: ViewId; snapshot: LiveSnapshot; mode: ExperienceMode; worldSummary: WorldSummary; systemPulse: ReturnType<typeof normalizeSystemPulseResponses>; operationsPulse: ReturnType<typeof normalizeOperationsPulse>; learningPulse: ReturnType<typeof normalizeLearningPulse>; proofPulse: ReturnType<typeof normalizeProofPulse>; capitalPulse: ReturnType<typeof normalizeCapitalPulse> }) {
  switch (activeView) {
    case "intelligence": return <IntelligenceView systemPulse={systemPulse}/>;
    case "capital": return <CapitalView capitalPulse={capitalPulse}/>;
    case "portfolio": return <Suspense fallback={<div className="nexus-world-loading"><Layers3/><strong>Portfolio Twin wird aufgebaut</strong><span>Positionen, Szenarien und Dossiers werden geladen …</span></div>}><DemoPortfolioWorld/></Suspense>;
    case "agents": return <AgentsView/>;
    case "operations": return <OperationsView snapshot={snapshot} mode={mode} systemPulse={systemPulse} operationsPulse={operationsPulse}/>;
    case "learning": return <LearningView systemPulse={systemPulse} learningPulse={learningPulse}/>;
    case "proof": return <ProofView snapshot={snapshot} systemPulse={systemPulse} proofPulse={proofPulse}/>;
    default: return <PulseView snapshot={snapshot} mode={mode} worldSummary={worldSummary} systemPulse={systemPulse}/>;
  }
}

function HqsSidebar({
  activeView,
  onViewChange,
  experienceMode,
}: {
  activeView: ViewId;
  onViewChange: (view: ViewId) => void;
  experienceMode: ExperienceMode;
}) {
  return (
    <Sidebar collapsible="icon" className="hqs-sidebar">
      <SidebarHeader className="sidebar-brand">
        <div className="brand-mark"><span>H</span><i/><b/></div>
        <div className="brand-copy"><strong>HQS</strong><span>PRIVATE QUANT</span><small>NEXUS OS</small></div>
      </SidebarHeader>
      <SidebarSeparator/>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="sidebar-label">{experienceMode === "buyer" ? "BUYER JOURNEY" : "ARBEITSBEREICHE"}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {experienceMode === "buyer" ? BUYER_CHAPTERS.map((item) => {
                const Icon = item.icon;
                return <SidebarMenuItem key={item.id}><SidebarMenuButton tooltip={item.label} onClick={() => document.getElementById(item.id)?.scrollIntoView({behavior:"smooth",block:"start"})} className="hqs-menu-button buyer-menu-button"><Icon/><span>{item.label}</span><small>{item.meta}</small></SidebarMenuButton></SidebarMenuItem>;
              }) : NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                return <SidebarMenuItem key={item.id}><SidebarMenuButton isActive={activeView === item.id} tooltip={item.label} onClick={() => onViewChange(item.id)} className="hqs-menu-button"><Icon/><span><strong>{item.label}</strong><em>{item.question}</em></span><small>{item.meta}</small></SidebarMenuButton></SidebarMenuItem>;
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel className="sidebar-label">TRUTH STATES</SidebarGroupLabel>
          <SidebarGroupContent><div className="sidebar-proof-states">{PROOF_LEGEND.map((item) => <span key={item.state}><i className={item.state}/><strong>{item.label}</strong><small>{item.state === "pending" ? "OPEN" : "VERIFIED"}</small></span>)}</div></SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="sidebar-footer-card">
        <div className={`operator-avatar operator-avatar--${experienceMode}`}>{experienceMode === "buyer" ? <Eye/> : experienceMode === "owner" ? "DH" : <KeyRound/>}</div>
        <div><strong>{experienceMode === "buyer" ? "Buyer Briefing" : experienceMode === "owner" ? "David Hücker" : "HQS Operator"}</strong><span>{experienceMode === "buyer" ? "Guided System View" : experienceMode === "owner" ? "System Owner" : "Deep Operations"}</span></div><ArrowUpRight/>
      </SidebarFooter>
      <SidebarRail/>
    </Sidebar>
  );
}

function ModeSwitch({ mode, onChange }: { mode: ExperienceMode; onChange: (mode: ExperienceMode) => void }) {
  return (
    <ToggleGroup type="single" value={mode} onValueChange={(value) => { if (value === "buyer" || value === "owner" || value === "operator") onChange(value); }} className="experience-switch" aria-label="Ansichtsmodus">
      <ToggleGroupItem value="buyer" aria-label="Käufer-Modus"><Eye/><span>Käufer</span></ToggleGroupItem>
      <ToggleGroupItem value="owner" aria-label="Owner-Modus"><Gauge/><span>Owner</span></ToggleGroupItem>
      <ToggleGroupItem value="operator" aria-label="Operator-Modus"><KeyRound/><span>Operator</span></ToggleGroupItem>
    </ToggleGroup>
  );
}

function OwnerWorkspaceOverview({ activeView, data, bridgeState, autoRefresh, onAutoRefreshChange, onNavigate }: {
  activeView: ViewId;
  data: NexusWorldData;
  bridgeState: BridgeState;
  autoRefresh: boolean;
  onAutoRefreshChange: (enabled: boolean) => void;
  onNavigate: (view: ViewId) => void;
}) {
  const attention = data.results.filter((result) => result.state !== "live" && result.state !== "checking" && result.state !== "empty");
  const activeItem = NAV_ITEMS.find((item) => item.id === activeView)!;
  return (
    <section className="owner-workspace-overview" aria-label="Owner-Arbeitsplatz">
      <div className="owner-workspace-overview__heading">
        <div><span className="section-kicker">HQS / OWNER WORKSPACE</span><h2>Dein System. <span>Im Blick.</span></h2><p>{activeItem.label} <span aria-hidden="true">/</span> {activeItem.question}</p></div>
        <button type="button" className="owner-auto-refresh" aria-pressed={autoRefresh} onClick={() => onAutoRefreshChange(!autoRefresh)}><span className={autoRefresh ? "is-on" : ""}/><span>Übersicht automatisch<small>{autoRefresh ? "Alle 60 s im sichtbaren Tab" : "Pausiert · manuell verfügbar"}</small></span></button>
      </div>
      <div className="owner-workspace-metrics">
        <div className={`owner-workspace-metric owner-workspace-metric--${bridgeState}`}><Network/><span>Datenverbindung<strong>{bridgeState === "connected" ? "Verbunden" : bridgeState === "unknown" ? "Wird geprüft" : "Eingeschränkt"}</strong><small>System Pulse</small></span></div>
        <div className="owner-workspace-metric"><Database/><span>Aktueller Bereich<strong>{data.updatedAt ? <>{data.summary.live}<em> / {data.summary.total} Quellen</em></> : "—"}</strong><small>{data.loading ? "Wird aktualisiert …" : "Vollständig verfügbare Daten"}</small></span></div>
        <div className={`owner-workspace-metric ${attention.length ? "owner-workspace-metric--attention" : ""}`}><AlertTriangle/><span>Aufmerksamkeit<strong>{data.updatedAt ? attention.length : "—"}<em> Quellen</em></strong><small>{attention.length ? "Details in der Datenlage" : data.updatedAt ? `${data.summary.empty} leere Antworten separat erfasst` : "Erster Abruf steht aus"}</small></span></div>
        <div className="owner-workspace-metric"><History/><span>Letzter Bereichsabruf<strong>{data.updatedAt ? formatTime(data.updatedAt) : "—"}</strong><small>UTC · {data.loading ? "Abruf läuft" : "abgeschlossener Abruf"}</small></span></div>
      </div>
      <nav className="owner-workspace-shortcuts" aria-label="Owner-Schnellzugriff">
        <span>Direkt öffnen</span>
        {(["portfolio", "agents", "operations", "proof"] as ViewId[]).map((view) => {
          const item = NAV_ITEMS.find((entry) => entry.id === view)!;
          const Icon = item.icon;
          return <button key={view} type="button" aria-current={activeView === view ? "page" : undefined} onClick={() => onNavigate(view)}><Icon/><span>{item.label}</span><ArrowUpRight/></button>;
        })}
      </nav>
    </section>
  );
}

export default function HqsCommandCenter({ onSignOut, signingOut = false, signOutError = "" }: { onSignOut?: () => void; signingOut?: boolean; signOutError?: string }) {
  const [activeView, setActiveView] = useState<ViewId>("pulse");
  const [experienceMode, setExperienceMode] = useState<ExperienceMode>("owner");
  const [snapshot, setSnapshot] = useState<LiveSnapshot>(INITIAL_SNAPSHOT);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  // The ONE canonical source for System Pulse's seven diagnostic endpoints —
  // always active regardless of which world the user is currently browsing
  // (the topbar DATA BRIDGE pill and command sheet must reflect System
  // Pulse itself, not "whatever world is on screen"). <LiveWorldPanel/>
  // reuses this exact instance (via `sharedData`) whenever `activeView` is
  // "pulse" instead of firing a second, independent fetch of the same seven
  // endpoints — see LiveWorldPanel.tsx's `sharedData` prop doc comment.
  const pulseWorldData = useNexusWorldData("pulse", { autoLoad: false });
  // fix/pnl-dossier-mapping-and-live-truth: same "always-on, reused via
  // sharedData" pattern as pulseWorldData above — the "operations"/"learning"
  // worlds were already being fetched by <LiveWorldPanel/> (their JSON just
  // never left its generic accordion), so lifting them here lets
  // OperationsView/LearningView read the real pipeline-status/db-capacity/
  // learning-diagnostics payloads instead of static placeholders, without a
  // second, redundant fetch of the same endpoints.
  const operationsWorldData = useNexusWorldData("operations", { autoLoad: false });
  const learningWorldData = useNexusWorldData("learning", { autoLoad: false });
  // fix/pnl-dossier-mapping-and-live-truth: same pattern again for the
  // "proof" world — /api/admin/performance-proof was already fetched here,
  // its real verifiedOutcomes/pendingOutcomes/proofScore numbers just never
  // reached ProofView's "Portfolio Outcome Proof" evidence row (a static
  // "pending" placeholder before this change).
  const proofWorldData = useNexusWorldData("proof", { autoLoad: false });
  // fix/pnl-dossier-mapping-and-live-truth: same pattern again for the
  // "capital" world — /api/admin/portfolio-twin was already fetched here
  // (WORLD_ENDPOINTS.capital), its real openCount just never reached
  // CapitalView's "ACTIVE" stat, which showed a static "8" captioned
  // "Illustrativer Paper-Snapshot" regardless of the real position count.
  const capitalWorldData = useNexusWorldData("capital", { autoLoad: false });
  const additionalWorldData = useNexusWorldData(activeView, { enabled: activeView === "intelligence" || activeView === "agents" || activeView === "portfolio" });
  const activeWorldData = activeView === "pulse" ? pulseWorldData : activeView === "operations" ? operationsWorldData : activeView === "learning" ? learningWorldData : activeView === "proof" ? proofWorldData : activeView === "capital" ? capitalWorldData : additionalWorldData;
  // The single normalized System Pulse state every cockpit surface reads
  // from — see src/nexus/systemPulse.js. `previousSystemPulseRef` carries a
  // partial/degraded refresh's still-valid prior numbers forward (never lets
  // a transient bad fetch blank out a known-good service count/universe
  // size/risk flag list — see `mergeSystemPulseStates`'s own doc comment).
  const previousSystemPulseRef = useRef<ReturnType<typeof normalizeSystemPulseResponses> | null>(null);
  const systemPulse = useMemo(() => {
    const next = normalizeSystemPulseResponses({ endpointResults: pulseWorldData.results });
    const merged = mergeSystemPulseStates(previousSystemPulseRef.current, next);
    previousSystemPulseRef.current = merged;
    return merged;
  }, [pulseWorldData.results]);
  const operationsPulse = useMemo(
    () => normalizeOperationsPulse({ endpointResults: operationsWorldData.results }),
    [operationsWorldData.results],
  );
  const learningPulse = useMemo(
    () => normalizeLearningPulse({ endpointResults: learningWorldData.results }),
    [learningWorldData.results],
  );
  const proofPulse = useMemo(
    () => normalizeProofPulse({ endpointResults: proofWorldData.results }),
    [proofWorldData.results],
  );
  const capitalPulse = useMemo(
    () => normalizeCapitalPulse({ endpointResults: capitalWorldData.results }),
    [capitalWorldData.results],
  );
  // Bumped on every refresh() call so a late-resolving request (e.g. the
  // periodic 60s poll firing again before a slow previous call returned)
  // can never overwrite state that a newer call has since produced —
  // mirrors the same generation-guard pattern used by
  // LiveWorldPanel's useNexusWorldData().
  const snapshotGenerationRef = useRef(0);
  const refreshInFlightRef = useRef(false);

  const refresh = useCallback(async (includeAdditionalWorld = true) => {
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    const generation = ++snapshotGenerationRef.current;
    setRefreshing(true);
    setRefreshError(null);
    // Refresh shared cockpit sources and the active diagnostic area together.
    // Specialized feature worlds retain their own controls.
    try {
      // One refresh owner; wait for every participating data source before
      // clearing the loading indicator. Additional worlds auto-load on entry.
      const [snapshotResult, ...worldResults] = await Promise.allSettled([
        adminFetch("/api/hqs-live", 20_000, { cache: "no-store" }),
        pulseWorldData.refresh(), operationsWorldData.refresh(), learningWorldData.refresh(),
        proofWorldData.refresh(), capitalWorldData.refresh(),
        ...(includeAdditionalWorld ? [additionalWorldData.refresh()] : []),
      ] as const);
      if (snapshotGenerationRef.current !== generation) return;
      if (snapshotResult.status === "rejected") throw snapshotResult.reason;
      const response = snapshotResult.value;
      if (response.error || !response.data) throw new Error(response.error || `HTTP ${response.status}`);
      // Merge onto the previous snapshot (never fully replace it) so a
      // malformed or partial /api/hqs-live payload can't clobber fields the
      // rest of the cockpit depends on (e.g. `readiness`/`guardian`) with
      // `undefined`.
      const payload = response.data as Partial<LiveSnapshot>;
      setSnapshot((previous) => ({
        ...previous,
        generatedAt: typeof payload.generatedAt === "string" ? payload.generatedAt : previous.generatedAt,
        source: typeof payload.source === "string" ? payload.source : previous.source,
        summary: payload.summary && typeof payload.summary.status === "number" ? payload.summary : previous.summary,
        readiness: payload.readiness && typeof payload.readiness.status === "number" ? payload.readiness : previous.readiness,
        guardian: payload.guardian && typeof payload.guardian.status === "number" ? payload.guardian : previous.guardian,
        infrastructure: {
          railway: { ...previous.infrastructure.railway, ...(payload.infrastructure?.railway ?? {}) },
          vercel: { ...previous.infrastructure.vercel, ...(payload.infrastructure?.vercel ?? {}) },
        },
        hasFetchedOnce: true,
      }));
      if (worldResults.some((result) => result.status === "rejected")) setRefreshError("Ein Datenbereich konnte nicht aktualisiert werden. Bitte die Datenlage prüfen.");
    } catch (error) {
      if (snapshotGenerationRef.current !== generation) return;
      setRefreshError(error instanceof Error ? error.message : "Live-Abfrage fehlgeschlagen");
      // A failed attempt is still a real, truthful outcome — record that we
      // have genuinely checked at least once so the UI stops showing the
      // neutral "—"/"UNKNOWN" pre-fetch placeholders and instead shows the
      // real (degraded) state.
      setSnapshot((previous) => ({ ...previous, hasFetchedOnce: true }));
    } finally {
      if (snapshotGenerationRef.current === generation) {
        refreshInFlightRef.current = false;
        setRefreshing(false);
      }
    }
  }, [pulseWorldData.refresh, operationsWorldData.refresh, learningWorldData.refresh, proofWorldData.refresh, capitalWorldData.refresh, additionalWorldData.refresh]);

  const refreshRef = useRef(refresh);
  useEffect(() => { refreshRef.current = refresh; }, [refresh]);

  useEffect(() => {
    const initialRefresh = window.setTimeout(() => void refreshRef.current(false), 0);
    return () => {
      window.clearTimeout(initialRefresh);
      snapshotGenerationRef.current += 1;
      refreshInFlightRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = window.setInterval(() => {
      if (!document.hidden) void refreshRef.current();
    }, 60_000);
    return () => window.clearInterval(interval);
  }, [autoRefresh]);

  // "DATA BRIDGE" (topbar pill, command sheet) is the SAME signal as
  // PulseView's "Read Path" metric tile — both are `systemPulse.bridgeState`
  // mapped to their own display vocabulary, never independently computed.
  const bridgeState = systemPulse.bridgeState;

  const handlePointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "touch") return;
    const bounds = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty("--pointer-x", `${event.clientX - bounds.left}px`);
    event.currentTarget.style.setProperty("--pointer-y", `${event.clientY - bounds.top}px`);
  }, []);

  const handleViewChange = useCallback((view: ViewId) => {
    runNexusViewTransition(() => { setActiveView(view); if (experienceMode === "buyer") setExperienceMode("owner"); });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [experienceMode]);

  const handleModeChange = useCallback((mode: ExperienceMode) => {
    runNexusViewTransition(() => { setExperienceMode(mode); if (mode === "operator") setActiveView("agents"); else if (mode !== "buyer") setActiveView("pulse"); });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleNotificationNavigate = useCallback((targetView: string, notification: { action?: { agentId?: string | null } }) => {
    if (notification.action?.agentId) {
      handleViewChange("agents");
      return;
    }
    const safeView = NAV_ITEMS.some((item) => item.id === targetView) ? targetView as ViewId : "pulse";
    handleViewChange(safeView);
  }, [handleViewChange]);

  return (
    <TooltipProvider delayDuration={120}>
      <SidebarProvider defaultOpen className="nexus-owner-workspace">
        <a className="owner-skip-link" href="#owner-content">Zum Arbeitsbereich</a>
        <div className="ambient-field" aria-hidden="true"><i className="ambient-field__cyan"/><i className="ambient-field__gold"/><i className="ambient-field__violet"/><i className="ambient-field__grid"/></div>
        <HqsSidebar activeView={activeView} onViewChange={handleViewChange} experienceMode={experienceMode}/>
        <SidebarInset className="hqs-main" data-view={experienceMode === "buyer" ? "buyer" : activeView} data-experience={experienceMode}>
          <div className="interaction-field" onPointerMove={handlePointerMove}>
            <header className="topbar">
              <span className="topbar-spectrum" aria-hidden="true"/>
              <div className="topbar__left"><SidebarTrigger className="sidebar-toggle"/><div className="breadcrumb"><span>HQS / NEXUS</span><ChevronRight/><strong>{experienceMode === "buyer" ? "BUYER BRIEFING" : VIEW_TITLES[activeView]}</strong></div></div>
              <div className="topbar__right">
                <ModeSwitch mode={experienceMode} onChange={handleModeChange}/>
                <Tooltip><TooltipTrigger asChild><div className={`bridge-pill bridge-pill--${bridgeState}`}><StatusDot tone={bridgeState === "connected" ? "good" : bridgeState === "unknown" ? "neutral" : "bad"}/><span>DATA BRIDGE</span><strong>{bridgeState === "connected" ? "CONNECTED" : bridgeState === "unknown" ? "PRÜFUNG" : "DEGRADED"}</strong></div></TooltipTrigger><TooltipContent side="bottom">Live-Verbindung zwischen Frontend und HQS Read-Models</TooltipContent></Tooltip>
                <span className="topbar-clock">{formatTime(snapshot.generatedAt)} UTC</span>
                <NexusNotificationCenter onNavigate={handleNotificationNavigate}/>
                {onSignOut ? <Button variant="ghost" size="icon-sm" onClick={onSignOut} disabled={signingOut} aria-label={signingOut ? "Abmeldung läuft" : "Sicher abmelden"} title="Abmelden"><LogOut/></Button> : null}
                <Button variant="outline" size="icon-sm" className="refresh-button" onClick={() => void refresh()} disabled={refreshing} aria-label="Übersicht und Datenlage aktualisieren"><RefreshCw className={refreshing ? "spin" : ""}/></Button>
                <Sheet>
                  <SheetTrigger asChild><Button size="sm" className="command-button"><Zap/><span>Command</span></Button></SheetTrigger>
                  <SheetContent className="command-sheet">
                    <SheetHeader className="command-sheet__header"><span className="section-kicker">HQS NEXUS NAVIGATOR</span><SheetTitle>Command Surface</SheetTitle><SheetDescription>Öffne Systemwelten, prüfe Beweiszustände und springe direkt zur relevanten Lage.</SheetDescription></SheetHeader>
                    <div className={`command-sheet__status command-sheet__status--${bridgeState}`}><div><StatusDot tone={bridgeState === "connected" ? "good" : bridgeState === "unknown" ? "neutral" : "bad"}/><span>DATA BRIDGE</span></div><strong>{bridgeState.toUpperCase()}</strong><small>Frontend Summary · HTTP {snapshot.summary.status || "OFF"}</small></div>
                    <nav className="command-sheet__nav" aria-label="HQS Systemwelten">{NAV_ITEMS.map((item) => { const Icon = item.icon; return <SheetClose asChild key={item.id}><button type="button" className={activeView === item.id ? "active" : ""} onClick={() => handleViewChange(item.id)}><span className="command-sheet__nav-icon"><Icon/></span><span><strong>{item.label}</strong><small>{item.question}</small></span><em>{item.meta}</em><ChevronRight/></button></SheetClose>; })}</nav>
                    <div className="command-sheet__proof"><span className="section-kicker">TRUTH STATES</span>{PROOF_LEGEND.map((item) => <div key={item.state}><ProofChip state={item.state} compact/><span>{item.detail}</span></div>)}</div>
                    <div className="command-sheet__footer"><div><span>Letzte Abfrage</span><strong>{formatDateTime(snapshot.generatedAt)} UTC</strong></div><Button variant="outline" size="sm" onClick={() => void refresh()} disabled={refreshing}><RefreshCw className={refreshing ? "spin" : ""}/>Live prüfen</Button></div>
                  </SheetContent>
                </Sheet>
              </div>
            </header>

            <div className="context-ribbon">
              <NexusLiveFabric bridgeState={bridgeState} activeView={activeView} onNavigate={(view) => handleViewChange(NAV_ITEMS.some((item) => item.id === view) ? view as ViewId : "pulse")}/>
              <div className="owner-snapshot-label"><History/><span>Aktueller Datenstand</span><strong>{formatTime(snapshot.generatedAt)} UTC</strong></div>
              <div className="context-ribbon__policy"><LockKeyhole/> CAPITAL <strong>PAPER LOCK</strong></div>
            </div>

            {experienceMode === "buyer" ? <div className="mobile-view-switcher buyer-mobile-switcher" role="navigation" aria-label="Käufer-Kapitel">{BUYER_CHAPTERS.map((item) => { const Icon = item.icon; return <button type="button" key={item.id} onClick={() => document.getElementById(item.id)?.scrollIntoView({behavior:"smooth",block:"start"})}><Icon/><span>{item.meta}</span></button>; })}</div> : <div className="mobile-view-switcher" role="navigation" aria-label="Systemwelten">{NAV_ITEMS.map((item) => { const Icon = item.icon; return <button type="button" key={item.id} className={activeView === item.id ? "active" : ""} onClick={() => handleViewChange(item.id)} aria-current={activeView === item.id ? "page" : undefined}><Icon/><span>{item.shortLabel}</span></button>; })}</div>}

            <div className="hqs-content" id="owner-content" tabIndex={-1}>
              {refreshError || signOutError ? <div className="owner-workspace-alert" role="alert"><AlertTriangle/><div><strong>{signOutError ? "Abmeldung nicht abgeschlossen" : "Aktualisierung unvollständig"}</strong><p>{signOutError || refreshError}</p></div><Button variant="outline" size="sm" onClick={signOutError ? onSignOut : () => void refresh()} disabled={signOutError ? signingOut : refreshing}>Erneut versuchen</Button></div> : null}
              {experienceMode === "buyer" ? <BuyerExperience snapshot={snapshot} onEnterOwner={() => handleModeChange("owner")} systemPulse={systemPulse}/> : <><OwnerWorkspaceOverview activeView={activeView} data={activeWorldData} bridgeState={bridgeState} autoRefresh={autoRefresh} onAutoRefreshChange={setAutoRefresh} onNavigate={handleViewChange}/><LiveWorldPanel world={activeView} sharedData={activeWorldData}/><ActiveView activeView={activeView} snapshot={snapshot} mode={experienceMode} worldSummary={pulseWorldData.summary} systemPulse={systemPulse} operationsPulse={operationsPulse} learningPulse={learningPulse} proofPulse={proofPulse} capitalPulse={capitalPulse}/></>}
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  );
}
